<script setup lang="ts">
// Vue & Plugins
import { Ref, computed, ref, useTemplateRef, watchEffect } from 'vue'

import { useQueryClient } from '@tanstack/vue-query'
import { refDebounced } from '@vueuse/core'
import { DateTime } from 'luxon'

import InfiniteScrollWrapper from '../../../common/components/InfiniteScrollWrapper.vue'
import EmployeeShiftOfferEligibleSelect from '../../../employee_shift/components/inputs/EmployeeShiftOfferEligibleSelect.vue'
import ShiftOfferTable from '../../../shift_offers/components/tables/ShiftOfferTable.vue'
import RoundStatusCard from '../../../shift_queue_alt/components/RoundStatusCard.vue'

import { useEmployeeShiftOfferAddMutation } from '../../../employee_shift/composables/useEmployeeShiftOffer'
// Composables
import { useEmployeeByShiftQueryInfinite } from '../../../employees/composables/useEmployee'
import { employeeKeys } from '../../../employees/composables/useEmployeeQuery'
import { useFeatureFlag } from '../../../features/composables/useFeatureFlag'
import { useShiftRemindMutation } from '../../composables/useShift'
import { useShiftAction } from '../../composables/useShiftAction'

import {
  EmployeeShiftOffer,
  ShiftOfferStatus,
} from '../../../employee_shift/models/employeeShiftOffer'
import { Employee } from '../../../employees/models/employee'
// Models
import { SiteID } from '../../../sites/models/site'
import { Shift } from '../../models/shift'

const { siteId, shift } = defineProps<{
  siteId: SiteID
  shift: Shift
}>()

const nameInput = ref('')
const name = refDebounced(nameInput, 500)

type FilterOfferStatus = 'all' | ShiftOfferStatus
const statusOptions: {
  label: string
  status: FilterOfferStatus
}[] = [
  { label: 'All', status: 'all' },
  { label: 'Applied', status: 'applied' },
  { label: 'Pending', status: 'pending' },
  { label: 'Queued', status: 'queued' },
]
const status: Ref<{ label: string; status: FilterOfferStatus }> = ref({
  label: 'All',
  status: 'all',
})
const statusFilter = computed(() => status.value.status)

const { data, error, isFetching, hasNextPage, fetchNextPage } =
  useEmployeeByShiftQueryInfinite(
    () => siteId,
    () => shift.id,
    {
      name: name,
      status: statusFilter,
      perPage: 25,
    },
  )
const offers = computed(
  () =>
    data?.value?.pages.reduce((acc, curr) => {
      return acc.concat(curr.offers)
    }, [] as EmployeeShiftOffer[]) ?? [],
)

const queryClient = useQueryClient()
const refresh = () => {
  queryClient.invalidateQueries({ queryKey: employeeKeys.lists() })
}

const selectedEmployee = ref<Employee | null>(null)
const addTooltip = computed(() =>
  shift.open
    ? 'This shift is marked as open, all eligible employees can automatically see and respond to it. You cannot manually add employees.'
    : '',
)
const addDisabled = computed(() => shift.open)
const isAddEmployeeValid = computed(() => selectedEmployee.value !== null)
const op = ref()
const toggle = (event: Event) => op.value.toggle(event)

const { mutate: addEmployee, isPending: isAddEmployeePending } =
  useEmployeeShiftOfferAddMutation(
    () => siteId,
    () => shift.id,
  )
const addEmployeePressed = () => {
  if (selectedEmployee.value) {
    addEmployee({
      form: {
        employeeId: selectedEmployee.value.id,
      },
    })
  }
}

const { mutate: remind, isPending: isRemindPending } = useShiftRemindMutation(
  () => siteId,
)
const remindPressed = () => {
  remind(
    { shiftId: shift.id, form: { method: remindPreferredMethod.value } },
    {
      onSuccess: () => {
        remindPopover.value?.hide()
      },
    },
  )
}

const isRecentlyReminded = computed(() => {
  const remindedAt = shift.remindedAt

  if (!remindedAt) {
    return false
  }

  return true
})
const remindTooltip = computed(() =>
  isRecentlyReminded.value
    ? 'You have already reminded employees about this shift.'
    : '',
)

const { canEdit } = useShiftAction(() => shift)
const showRemindButton = computed(() => {
  return canEdit.value && shift.published && !shift.rosterEmployeeId
})

type RemindMethod = 'push' | 'email' | 'sms'
// SMS only where the operator turned it on for the site (changes-1019 CHG-28)
const smsOn = useFeatureFlag('sms')
const remindMethods = computed<{ label: string; value: RemindMethod }[]>(() => [
  { label: 'Push Notification', value: 'push' },
  { label: 'Email', value: 'email' },
  ...(smsOn.value ? [{ label: 'SMS', value: 'sms' as const }] : []),
])
const remindPreferredMethod: Ref<RemindMethod> = ref('push')
watchEffect(() => {
  if (!smsOn.value && remindPreferredMethod.value === 'sms') remindPreferredMethod.value = 'push'
})

const remindPopover = useTemplateRef('remind-popover')
const toggleRemindPopover = (event: Event) => {
  if (!remindPopover.value) {
    return
  }

  remindPopover.value.toggle(event)
}

const offersListHeightClass = computed(() => {
  // TODO: Fix height calculation to use actual size from components and their spacing
  //  header ~h-16
  //  content ~h-14
  //  panel wrapper ~h-48
  //  panel header ~h-8
  return 'h-[calc(100vh-theme(height.16)-theme(height.14)-theme(height.48)-theme(height.32))]'
})

const queuePopover = useTemplateRef('queue-popover')
const toggleQueuePopover = (event: Event) => {
  if (!queuePopover.value) {
    return
  }

  queuePopover.value.toggle(event)
}

const now = ref(DateTime.now())

const nextRound = computed(() => {
  if (!shift.queue) return undefined

  return shift.request_rounds.filter((round) => !round.firedAt).at(0)
})
</script>

<template>
  <div>
    <Toolbar>
      <template #start>
        <div class="flex items-center gap-x-2">
          <InputText v-model="nameInput" label="Search" placeholder="Search" />

          <Select
            v-model="status"
            :options="statusOptions"
            option-label="label"
          />
        </div>
      </template>

      <template #center>
        <Button
          v-if="showRemindButton"
          v-tooltip="remindTooltip"
          icon="pi pi-bell"
          label="Remind"
          severity="warn"
          outlined
          :loading="isRemindPending"
          :disabled="isRecentlyReminded"
          @click="toggleRemindPopover"
        />

        <Popover ref="remind-popover">
          <div class="flex flex-col gap-4 min-w-48">
            <Select
              v-model="remindPreferredMethod"
              :options="remindMethods"
              :option-label="'label'"
              :option-value="'value'"
            />

            <Button
              v-tooltip="remindTooltip"
              label="Send"
              outlined
              :loading="isRemindPending"
              :disabled="isRecentlyReminded"
              @click="remindPressed"
            />
          </div>
        </Popover>
      </template>

      <template #end>
        <div class="flex gap-x-2">
          <Button
            v-if="shift.queue"
            icon="pi pi-clock"
            text
            @click="toggleQueuePopover"
          />

          <Popover v-if="shift.queue" ref="queue-popover">
            <div class="flex flex-col gap-1 max-h-96 overflow-hidden">
              <div class="flex flex-col gap-y-2 overflow-auto">
                <RoundStatusCard
                  v-for="round in shift.request_rounds ?? []"
                  :key="round.id"
                  class="mt-2"
                  :request-round="round"
                  :now="now"
                />
              </div>
            </div>
          </Popover>

          <Button
            icon="pi pi-refresh"
            text
            :loading="isFetching"
            @click="refresh"
          />

          <Button
            v-if="canEdit"
            v-tooltip="addTooltip"
            icon="pi pi-plus"
            :loading="isFetching"
            :disabled="addDisabled"
            @click="toggle"
          />

          <Popover ref="op">
            <div class="flex grid-cols-3 items-center gap-x-4">
              <div>
                <EmployeeShiftOfferEligibleSelect
                  v-if="!isFetching && shift.area"
                  v-model:selected="selectedEmployee"
                  :site-id="siteId"
                  :area-id="shift.area.id"
                  :start-date="shift.startDate"
                  :end-date="shift.endDate"
                  :shift-id="shift.id"
                />
              </div>

              <div class="place-self-end">
                <Button
                  label="Add"
                  severity="secondary"
                  :disabled="!isAddEmployeeValid"
                  :loading="isAddEmployeePending"
                  @click="addEmployeePressed"
                />
              </div>
            </div>
          </Popover>
        </div>
      </template>
    </Toolbar>

    <div v-if="nextRound" class="flex flex-col gap-y-2 mt-2">
      <h6 class="text-lg font-semibold">Next Queue Round</h6>
      
      <RoundStatusCard :request-round="nextRound" :now="now" />
    </div>

    <InfiniteScrollWrapper
      class="mt-2"
      :is-empty="offers.length === 0"
      :is-fetching="isFetching"
      :has-next-page="hasNextPage"
      :error="error?.message"
      :height-class="offersListHeightClass"
      @load-more="fetchNextPage"
    >
      <template #default>
        <ShiftOfferTable :site-id="siteId" :shift="shift" :offers="offers" />
      </template>
    </InfiniteScrollWrapper>
  </div>
</template>
